import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Database, Plus, RefreshCw, ChevronLeft, ChevronRight, ClipboardList } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth, PERMISSIONS } from '@/contexts/AuthContext'
import { fetchDatabases, queryDocuments } from '../api/mongoConsole'
import { CollectionBrowser } from '../components/CollectionBrowser'
import { QueryPanel } from '../components/QueryPanel'
import { DocumentModal } from '../components/DocumentModal'
import { InsertDocumentModal } from '../components/InsertDocumentModal'
import { AuditLogPanel } from '../components/AuditLogPanel'
import type { CollectionInfo, QueryRequest, QueryResponse } from '../types/mongoConsole.types'

type TabType = 'data' | 'audit'

export function MongoConsolePage() {
  const { hasPermission } = useAuth()
  const canDelete = hasPermission(PERMISSIONS.MONGO_CONSOLE_DELETE)

  const [selectedCollection, setSelectedCollection] = useState<CollectionInfo | null>(null)
  const [queryResult, setQueryResult] = useState<QueryResponse | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('data')
  const [selectedDocument, setSelectedDocument] = useState<Record<string, unknown> | null>(null)
  const [showInsert, setShowInsert] = useState(false)

  // Fetch databases + collections
  const { data: databases = [], isLoading: dbLoading, refetch: refetchDbs } = useQuery({
    queryKey: ['mongo-databases'],
    queryFn: fetchDatabases,
    staleTime: 60_000,
  })

  // Query mutation
  const queryMutation = useMutation({
    mutationFn: (req: QueryRequest) => queryDocuments(req),
    onSuccess: (data) => setQueryResult(data),
  })

  const handleSelectCollection = (col: CollectionInfo) => {
    setSelectedCollection(col)
    setQueryResult(null)
    // Auto-run default query
    queryMutation.mutate({ database: col.database, collection: col.name, limit: 20, skip: 0 })
  }

  const handlePageChange = (newSkip: number) => {
    if (!queryResult || !selectedCollection) return
    queryMutation.mutate({
      database: queryResult.database,
      collection: queryResult.collection,
      limit: queryResult.limit,
      skip: newSkip,
    })
  }

  return (
    <div className="flex h-full" style={{ height: 'calc(100vh - 64px)' }}>
      {/* Left sidebar — collection browser */}
      <aside className="w-56 shrink-0 border-r bg-gray-50 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
            <Database className="h-4 w-4 text-blue-500" />
            Collections
          </div>
          <button onClick={() => refetchDbs()} title="Refresh" className="text-gray-400 hover:text-gray-600">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <CollectionBrowser
            databases={databases}
            isLoading={dbLoading}
            selectedCollection={selectedCollection}
            onSelect={handleSelectCollection}
          />
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Page header + tabs */}
        <div className="border-b bg-white px-5 pt-4 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900">MongoDB Console</h1>
              {selectedCollection && (
                <p className="text-xs text-gray-400 font-mono mt-0.5">
                  {selectedCollection.database}.{selectedCollection.name}
                </p>
              )}
            </div>
            {canDelete && selectedCollection && activeTab === 'data' && (
              <Button size="sm" onClick={() => setShowInsert(true)}>
                <Plus className="h-4 w-4 mr-1.5" />
                Insert Document
              </Button>
            )}
          </div>
          <nav className="flex gap-6">
            {(['data', 'audit'] as TabType[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'data' ? (
                  <span className="flex items-center gap-1.5"><Database className="h-3.5 w-3.5" />Data</span>
                ) : (
                  <span className="flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5" />Audit Log</span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'audit' ? (
          <div className="flex-1 overflow-auto bg-white">
            <AuditLogPanel />
          </div>
        ) : !selectedCollection ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <Database className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">Select a collection to begin</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden bg-white">
            {/* Query controls */}
            <div className="shrink-0">
              <QueryPanel
                collection={selectedCollection}
                onQuery={req => queryMutation.mutate(req)}
                isLoading={queryMutation.isPending}
              />
            </div>

            {/* Results */}
            <div className="flex-1 overflow-auto">
              {queryMutation.isPending && (
                <div className="p-8 text-center text-sm text-gray-400">Running query…</div>
              )}

              {queryMutation.isError && (
                <div className="p-6 text-center">
                  <p className="text-sm text-red-600 font-medium">Query failed</p>
                  <p className="text-xs text-gray-400 mt-1">{(queryMutation.error as Error).message}</p>
                </div>
              )}

              {queryResult && !queryMutation.isPending && (
                <>
                  {/* Result count bar */}
                  <div className="px-4 py-2 border-b bg-gray-50 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {queryResult.total.toLocaleString()} document{queryResult.total !== 1 ? 's' : ''}
                      {queryResult.skip > 0 && ` (showing ${queryResult.skip + 1}–${queryResult.skip + queryResult.documents.length})`}
                    </span>
                    {queryResult.total > queryResult.limit && (
                      <div className="flex items-center gap-2">
                        <button
                          disabled={queryResult.skip === 0}
                          onClick={() => handlePageChange(Math.max(0, queryResult.skip - queryResult.limit))}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span className="text-xs text-gray-500">
                          Page {Math.floor(queryResult.skip / queryResult.limit) + 1}
                        </span>
                        <button
                          disabled={!queryResult.hasMore}
                          onClick={() => handlePageChange(queryResult.skip + queryResult.limit)}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {queryResult.documents.length === 0 ? (
                    <div className="p-8 text-center text-sm text-gray-400">No documents match the query.</div>
                  ) : (
                    <DocumentResultsTable
                      documents={queryResult.documents}
                      onSelect={setSelectedDocument}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      {selectedDocument && selectedCollection && (
        <DocumentModal
          document={selectedDocument}
          database={selectedCollection.database}
          collection={selectedCollection.name}
          canWrite={true}
          canDelete={canDelete}
          onClose={() => setSelectedDocument(null)}
        />
      )}
      {showInsert && selectedCollection && (
        <InsertDocumentModal
          database={selectedCollection.database}
          collection={selectedCollection.name}
          onClose={() => setShowInsert(false)}
        />
      )}
    </div>
  )
}

// ── Document Results Table ─────────────────────────────────────────────────

interface ResultsTableProps {
  documents: Record<string, unknown>[]
  onSelect: (doc: Record<string, unknown>) => void
}

function DocumentResultsTable({ documents, onSelect }: ResultsTableProps) {
  // Derive column headers from first ~5 documents, limit to 8 columns
  const allKeys = new Set<string>()
  documents.slice(0, 5).forEach(doc => Object.keys(doc).forEach(k => allKeys.add(k)))
  const columns = ['_id', ...Array.from(allKeys).filter(k => k !== '_id')].slice(0, 8)

  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-gray-50 text-gray-500 text-left sticky top-0">
            {columns.map(col => (
              <th key={col} className="px-3 py-2 font-medium font-mono whitespace-nowrap">{col}</th>
            ))}
            <th className="px-3 py-2 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc, idx) => (
            <tr key={idx} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => onSelect(doc)}>
              {columns.map(col => (
                <td key={col} className="px-3 py-2 max-w-[200px]">
                  <span className="font-mono truncate block" title={String(doc[col] ?? '')}>
                    {formatCellValue(doc[col])}
                  </span>
                </td>
              ))}
              <td className="px-3 py-2 text-right">
                <button
                  onClick={e => { e.stopPropagation(); onSelect(doc) }}
                  className="text-blue-600 hover:underline text-xs"
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value).slice(0, 60)
  return String(value).slice(0, 80)
}
